import { useMemo, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  applyDeliveredItemAdjustment,
  DELIVERED_ITEM_ADJUSTMENT_LABELS,
  type DeliveredItemAdjustmentType,
} from "@shared/procurementDeliveredItems";

const STAFF_ADJUSTMENT_TYPES: DeliveredItemAdjustmentType[] = [
  "quantity_correction",
  "quantity_returned",
  "quantity_added",
  "description_correction",
  "uom_correction",
  "serial_returned",
];

type StaffItem = {
  deliveryLineId: number;
  description: string;
  quantity: number;
  uom: string | null;
  serials: string[];
  serialWarranties?: Array<{
    serial: string;
    label: string | null;
    liveLink: string | null;
    staffCardHref?: string | null;
    staffReviewRequired?: boolean;
  }>;
  staffReviewNotices?: Array<{ serial: string; message: string; staffHref: string | null }>;
  originalQuantity?: number;
  originalDescription?: string;
  originalUom?: string | null;
  originalSerials?: string[];
  originalDeliveryDate: string | null;
  doReference?: string | null;
  documentId: number | null;
  projectProcurementId: number;
};

function newOccurrenceKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `occ-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function CustomerDeliveredItemsSection({ customerId }: { customerId: number }) {
  const query = trpc.customers.listDeliveredItems.useQuery({ customerId }, { retry: false });
  const legacyQuery = trpc.customers.listLegacyDeliveredItems.useQuery({ customerId }, { retry: false });
  const adjust = trpc.customers.applyDeliveredItemAdjustment.useMutation();
  const reportIssue = trpc.customers.reportDeliveredItemsIssue.useMutation();
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const [type, setType] = useState<DeliveredItemAdjustmentType>("quantity_correction");
  const [reason, setReason] = useState("");
  const [proposed, setProposed] = useState("");
  const [serial, setSerial] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [occurrenceKey, setOccurrenceKey] = useState(newOccurrenceKey());
  const [success, setSuccess] = useState(false);
  const submitLockRef = useRef(false);
  const history = trpc.customers.listDeliveredItemAdjustments.useQuery(
    { customerId, deliveryLineId: selectedLineId ?? 0 },
    { enabled: selectedLineId != null },
  );

  const selectedItem = useMemo(() => {
    for (const site of query.data?.sites ?? []) {
      const item = site.items.find((row) => row.deliveryLineId === selectedLineId);
      if (item) return item as StaffItem;
    }
    return null;
  }, [query.data, selectedLineId]);

  const resetForm = (opts?: { newOccurrence?: boolean }) => {
    setReason("");
    setProposed("");
    setSerial("");
    setConfirmed(false);
    setSuccess(false);
    submitLockRef.current = false;
    if (opts?.newOccurrence !== false) {
      setOccurrenceKey(newOccurrenceKey());
    }
  };

  const openAdjust = (lineId: number) => {
    setSelectedLineId(lineId);
    setType("quantity_correction");
    resetForm({ newOccurrence: true });
  };

  const startAnotherAdjustment = () => {
    resetForm({ newOccurrence: true });
  };

  const report = (input: {
    procurementId?: number;
    documentId?: number | null;
    workflowStage: string;
    safeErrorCode: string;
    operation: string;
  }) => {
    void reportIssue.mutateAsync({
      customerId,
      procurementId: input.procurementId,
      documentId: input.documentId ?? undefined,
      workflowStage: input.workflowStage,
      safeErrorCode: input.safeErrorCode,
      operation: input.operation,
    });
  };

  if (query.isLoading && !query.data) {
    return <p className="text-sm text-muted-foreground" data-testid="delivered-items-loading">Loading delivered items…</p>;
  }
  if (query.isError && !query.data) {
    return (
      <div data-testid="delivered-items-error">
        <p className="text-sm text-muted-foreground mb-3">We could not load delivered items.</p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => void query.refetch()}>Retry</Button>
          <Button
            type="button"
            variant="outline"
            data-testid="delivered-items-report-issue"
            onClick={() =>
              report({
                workflowStage: "customer_card_delivered_items",
                safeErrorCode: "list_load_failed",
                operation: "listDeliveredItems",
              })
            }
          >
            Report Issue
          </Button>
        </div>
      </div>
    );
  }
  if (!query.data?.sites.length && !legacyQuery.data?.length) {
    return <p className="text-sm text-muted-foreground" data-testid="delivered-items-empty">No delivered items.</p>;
  }

  const requested = (): Record<string, unknown> => {
    if (type === "description_correction") return { description: proposed };
    if (type === "uom_correction") return { uom: proposed };
    if (type === "serial_returned") return { serial };
    if (proposed.trim() === "") return {};
    return { resultingQuantity: Number(proposed) };
  };

  const preview = selectedItem
    ? applyDeliveredItemAdjustment({
        type,
        before: {
          quantity: selectedItem.quantity,
          uom: selectedItem.uom,
          description: selectedItem.description,
          serials: selectedItem.serials,
        },
        requested: requested(),
        reason,
        occurrenceKey,
      })
    : null;

  return (
    <div className="space-y-6 min-w-0 overflow-x-hidden" data-testid="delivered-items-section">
      {legacyQuery.data?.length ? (
        <section className="space-y-3" data-testid="legacy-delivered-items">
          <div>
            <h3 className="text-base font-medium">Pre-Portal deliveries</h3>
            <p className="text-xs text-muted-foreground">Historical Delivery Orders linked directly to this customer without a placeholder proposal.</p>
          </div>
          {legacyQuery.data.map(delivery => (
            <div key={delivery.id} className="rounded-lg border border-blue-200 bg-blue-50/30 p-4">
              <p className="text-sm font-medium">
                {delivery.reference || `Legacy Delivery Order #${delivery.murhoDocumentId}`}
              </p>
              <p className="text-xs text-muted-foreground">Delivered {String(delivery.deliveryDate)}</p>
              <div className="mt-3 space-y-2">
                {delivery.lines.map(line => (
                  <div key={line.id} className="rounded-md border bg-background p-3">
                    <p className="font-medium">{line.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {Number(line.quantity)} {line.uom || ""}
                      {line.finish ? ` · ${line.finish}` : ""}
                    </p>
                    {line.serials.map(serial => <p key={serial} className="text-sm">Serial: {serial}</p>)}
                    {line.warrantyCardIds.length ? (
                      <p className="text-xs text-emerald-800">Warranty draft created</p>
                    ) : line.createWarrantySelected ? (
                      <p className="text-xs text-amber-800">Warranty review required</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      ) : null}
      {query.isError && query.data ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="delivered-items-stale">
          <p className="text-sm text-amber-900 mb-2">Showing last loaded delivered items. These may not be the latest.</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={() => void query.refetch()}>Retry</Button>
            <Button
              type="button"
              variant="outline"
              data-testid="delivered-items-report-issue"
              onClick={() =>
                report({
                  procurementId: query.data.sites[0]?.items[0]?.projectProcurementId,
                  documentId: query.data.sites[0]?.items[0]?.documentId,
                  workflowStage: "customer_card_delivered_items",
                  safeErrorCode: "list_refresh_failed",
                  operation: "listDeliveredItems",
                })
              }
            >
              Report Issue
            </Button>
          </div>
        </div>
      ) : null}
      {(query.data?.sites ?? []).map((site) => (
        <section key={site.siteId} data-testid={`delivered-items-site-${site.siteId}`}>
          <h3 className="text-base font-medium mb-3">{site.label || site.address || `Site ${site.siteId}`}</h3>
          <div className="space-y-3">
            {site.items.map((item) => (
              <div key={item.deliveryLineId} className="rounded-lg border p-4 min-w-0">
                <p className="font-medium break-words">{item.description}</p>
                <p className="text-sm text-muted-foreground">
                  {item.quantity} {item.uom ?? ""} · Delivered {item.originalDeliveryDate ?? "—"}
                  {item.doReference ? ` · ${item.doReference}` : ""}
                </p>
                {(item.serialWarranties ?? item.serials.map((sn: string) => ({ serial: sn, label: null, liveLink: null, staffCardHref: null }))).map(
                  (row: {
                    serial: string;
                    label: string | null;
                    liveLink: string | null;
                    staffCardHref?: string | null;
                  }) => (
                    <p key={row.serial} className="text-sm">
                      Serial: {row.serial}
                      {row.label === "Warranty being prepared" ? " · Warranty being prepared" : ""}
                      {row.label === "Warranty live" ? " · Warranty live" : ""}
                      {row.staffCardHref ? (
                        <>
                          {" · "}
                          <a
                            href={row.staffCardHref}
                            className="underline"
                            data-testid={`delivered-items-staff-warranty-${row.serial}`}
                          >
                            View Warranty
                          </a>
                        </>
                      ) : null}
                    </p>
                  ),
                )}
                {(item.staffReviewNotices ?? []).map((notice: { serial: string; message: string; staffHref: string | null }) => (
                  <div
                    key={`review-${notice.serial}`}
                    className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2"
                    data-testid={`delivered-items-warranty-review-${notice.serial}`}
                  >
                    <p className="text-sm text-amber-950">{notice.message}</p>
                    {notice.staffHref ? (
                      <a href={notice.staffHref} className="text-sm underline">
                        Open warranty workspace
                      </a>
                    ) : (
                      <a href="/warranties" className="text-sm underline">
                        Open warranty workspace
                      </a>
                    )}
                  </div>
                ))}
                <div className="mt-3 flex flex-wrap gap-2">
                  {query.data?.canViewOriginalDo && item.documentId && item.projectProcurementId ? (
                    <OriginalDoButton
                      documentId={item.documentId}
                      procurementId={item.projectProcurementId}
                    />
                  ) : item.documentId ? (
                    <span className="text-xs text-muted-foreground">
                      Original DO restricted to authorised Procurement staff.
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Original DO unavailable</span>
                  )}
                  {query.data?.canMutate ? (
                    <Button type="button" variant="outline" onClick={() => openAdjust(item.deliveryLineId)}>
                      Adjust delivered item
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      {selectedLineId && selectedItem && query.data?.canMutate ? (
        <div className="rounded-lg border p-4 space-y-3" data-testid="delivered-items-adjust">
          <h4 className="font-medium">Adjust delivered item</h4>
          <span data-testid="delivered-items-occurrence" data-occurrence-key={occurrenceKey} className="hidden">
            {occurrenceKey}
          </span>
          <p className="text-sm text-muted-foreground" data-testid="delivered-items-original">
            Original: {selectedItem.originalQuantity ?? selectedItem.quantity} {selectedItem.originalUom ?? selectedItem.uom ?? ""} · {selectedItem.originalDescription ?? selectedItem.description}
          </p>
          <p className="text-sm" data-testid="delivered-items-current">
            Current: {selectedItem.quantity} {selectedItem.uom ?? ""} · {selectedItem.description}
          </p>
          <div>
            <Label htmlFor="adj-type">Adjustment</Label>
            <select
              id="adj-type"
              data-testid="delivered-items-type"
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
              value={type}
              disabled={success}
              onChange={(e) => {
                setType(e.target.value as DeliveredItemAdjustmentType);
                setProposed("");
                setSerial("");
                setConfirmed(false);
              }}
            >
              {STAFF_ADJUSTMENT_TYPES.map((value) => (
                <option key={value} value={value}>
                  {DELIVERED_ITEM_ADJUSTMENT_LABELS[value]}
                </option>
              ))}
            </select>
          </div>
          {type === "serial_returned" ? (
            <div>
              <Label htmlFor="adj-serial">Exact serial</Label>
              <select
                id="adj-serial"
                className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                value={serial}
                disabled={success}
                onChange={(e) => setSerial(e.target.value)}
              >
                <option value="">Select serial</option>
                {selectedItem.serials.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <Label htmlFor="adj-proposed">
                {type === "description_correction"
                  ? "Proposed description"
                  : type === "uom_correction"
                    ? "Proposed UOM"
                    : type === "quantity_correction"
                      ? "Corrected current quantity"
                      : "Proposed current quantity"}
              </Label>
              <Input id="adj-proposed" data-testid="delivered-items-proposed" value={proposed} disabled={success} onChange={(e) => setProposed(e.target.value)} />
            </div>
          )}
          <div>
            <Label htmlFor="adj-reason">Reason</Label>
            <Input id="adj-reason" data-testid="delivered-items-reason" value={reason} disabled={success} onChange={(e) => setReason(e.target.value)} />
          </div>
          {preview?.ok ? (
            <p className="text-sm" data-testid="delivered-items-preview">
              After: {preview.after.quantity} {preview.after.uom ?? ""} · {preview.after.description}
              {preview.after.serials.length ? ` · ${preview.after.serials.join(", ")}` : ""}
            </p>
          ) : preview && !preview.ok ? (
            <p className="text-sm text-destructive">{preview.message}</p>
          ) : null}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="delivered-items-confirm"
              checked={confirmed}
              disabled={success}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            I confirm this adjustment
          </label>
          <Button
            type="button"
            data-testid="delivered-items-submit"
            disabled={!reason.trim() || !confirmed || adjust.isPending || !preview?.ok || success}
            onClick={() => {
              if (submitLockRef.current || success || adjust.isPending) return;
              submitLockRef.current = true;
              void adjust
                .mutateAsync({
                  customerId,
                  deliveryLineId: selectedLineId,
                  type,
                  requested: requested(),
                  reason,
                  occurrenceKey,
                })
                .then(async () => {
                  await query.refetch();
                  await history.refetch();
                  setConfirmed(false);
                  setSuccess(true);
                })
                .catch(() => {
                  submitLockRef.current = false;
                  setSuccess(false);
                });
            }}
          >
            Confirm adjustment
          </Button>
          {adjust.isError ? (
            <div data-testid="delivered-items-adjust-error">
              <p className="text-sm text-destructive mb-2">The adjustment could not be saved. Your entries are still here.</p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => void adjust.reset()}>
                  Retry
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="delivered-items-report-issue"
                  onClick={() =>
                    report({
                      procurementId: selectedItem.projectProcurementId,
                      documentId: selectedItem.documentId,
                      workflowStage: "customer_card_delivered_items",
                      safeErrorCode: "adjustment_failed",
                      operation: "applyDeliveredItemAdjustment",
                    })
                  }
                >
                  Report Issue
                </Button>
              </div>
            </div>
          ) : null}
          {success ? (
            <div data-testid="delivered-items-success">
              <p className="text-sm text-green-700 mb-2">Adjustment saved.</p>
              <Button type="button" variant="outline" data-testid="delivered-items-another" onClick={startAnotherAdjustment}>
                Make another adjustment
              </Button>
            </div>
          ) : null}
          {history.data?.length ? (
            <div data-testid="delivered-items-history">
              <p className="text-sm font-medium mb-1">Internal adjustment history</p>
              {history.data.map((row) => (
                <p key={row.id} className="text-xs text-muted-foreground">
                  {row.adjustmentType}: {row.reason} · {row.actorNameSnapshot}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function OriginalDoButton({ documentId, procurementId }: { documentId: number; procurementId: number }) {
  const urlQ = trpc.procurement.getCustomerDeliveryDocumentDownloadUrl.useQuery(
    { documentId, procurementId },
    { enabled: false },
  );
  return (
    <Button
      type="button"
      variant="outline"
      onClick={async () => {
        const result = await urlQ.refetch();
        const url = result.data && "url" in result.data ? String((result.data as { url?: string }).url ?? "") : "";
        if (url) window.open(url, "_blank", "noopener,noreferrer");
      }}
    >
      View original DO
    </Button>
  );
}
